import { errorSafe } from 'app/infrastructure/redux';
import {
  ACTION, RequestCaseViewTableActionType, CaseViewTableResultAppendActionType, CaseViewTableResultReplaceActionType,
  CaseViewTableClearActionType,
  FailExecuteFavouriteActionType,
  CaseViewTableRemoveItemActionType,
  CaseViewTableRequestRemoveItemActionType,
  CaseViewTableRequestSingleSupplierUrlActionType,
  CaseViewTableReceiveSingleSupplierUrlActionType,
  CaseViewTableRequestLayoutModelActionType,
  CaseViewTableReceiveLayoutModelActionType
}
  from 'app/area/case-report/case-table-view/action-types';
import { api } from 'app/area/case-report/case-table-view/api';

import { api as caseListingApi } from 'app/area/case-report/case-listing/api';
import { CaseTableViewMetadata, PayloadCaseTableViewResultType } from 'app/area/case-report/case-table-view/types-store';
import { SummarySectionTag } from 'app/area/case-report/case-table-view/constants';
import { caseDataToStore } from 'app/infrastructure/store/normalize';
import uniqBy from 'lodash/uniqBy';
import { UiState } from 'app/infrastructure/ui/form/types';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { caseListingTranslation } from 'app/area/case-report/case-listing/constants';
import { ExecuteCaseTableViewReportType } from 'app/area/case-report/case-table-view/types-api';
import { AddRiskEvaluationInitialValues } from 'app/area/case/features/loss-potential-evaluation/types';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';
import { getUserMessage } from 'app/infrastructure/notifications/user-messages/get-user-message';
import { sectionId, tableName } from 'app/area/case/case-data-model';
import * as navigate from 'app/area/shared/navigation';
import { api as lossPotentialEvaluationApi } from 'app/area/case/features/loss-potential-evaluation/api';
import { ReceiveCopyRiskEvaluation, RequestCopyRiskEvaluation } from 'app/area/case/features/loss-potential-evaluation/action-types';
import { mapCaseTableViewDomains, mapColumnsToSummarySection } from 'app/area/case-report/case-table-view/map-summary-section-column';
import { ReportCategory } from 'app/area/case-report/case-listing/types';
import { isCaseViewModalOpen, shouldOpenCaseInModal } from 'app/components/helpers';
import { selectSelectedReport, selectLayoutTypeForSelectedReport, selectFavouriteId } from 'app/area/favourite/selectors';
import { selectCaseTableViewInstanceState } from 'app/area/case-report/case-table-view/selectors';
import { reportLayoutTypeContext } from 'app/area/reports/constants';

export const executeCaseTableViewReport = ({
  definition,
  instanceKey,
  startIndex = 0,
  maxResults = 50,
  overrideExecuteFavourite
}: ExecuteCaseTableViewReportType) => {
  return errorSafe(async (dispatch: any, getState: any) => {
    dispatch(RequestCaseViewTableAction(instanceKey));
    const requestId = selectCaseTableViewInstanceState(instanceKey)(getState())?.executeRequestId;
    
    try {
      let response;
      // Dashboard using different APIs to load the data
      if (overrideExecuteFavourite) {
        response = await overrideExecuteFavourite(definition, startIndex, maxResults, true);
      } else {
        response = await api.executeCaseTableViewReport(definition, startIndex, maxResults);
      }
      const { results, messages } = response;
      const replaceResult = !!startIndex;
      const { mainEntityTypePath = [], totalNumberOfResults } = response?.metadata || {};
      const includedColumns = mapColumnsToSummarySection(response.metadata?.includedFields);
      const filteredColumns = includedColumns.filter(field => field?.entityTypePath?.every(item => mainEntityTypePath.includes(item)) ||
        field.summarySectionTag).filter(field => 
          !(definition?.selectedReport?.v === ReportCategory.NumeratorReport && field.summarySectionTag === SummarySectionTag.Actions)
        );
      const uniqueFields = uniqBy(filteredColumns, 'columnName');
      const domains = mapCaseTableViewDomains(response.metadata?.domains);
      const metadata: CaseTableViewMetadata = { totalNumberOfResults, domains, includedFields: uniqueFields };

      const entities = results?.map(caseDataToStore);
      if (entities) {
        replaceResult ? dispatch(CaseViewTableResultAppendAction({
          instanceKey,
          requestId,
          caseTableViewResults: {
            entities,
            metadata,
            messages
          }
        })) :
          dispatch(CaseViewTableResultReplaceAction({
            instanceKey,
            requestId,
            caseTableViewResults: {
              entities,
              metadata,
              messages
            }
          }));
      }
      else {
        dispatch(CaseViewTableResultAppendAction({
          instanceKey,
          requestId,
          caseTableViewResults: {
            entities: [],
            metadata,
            messages
          }
        }));
      }
    }
    catch (error) {
      if (error?.userMessages?.length) {
        dispatch(CaseViewTableFailExecuteFavouriteAction(instanceKey, error.userMessages.map(m => m.message), requestId));
      }
    }
  });
};

export const deleteCaseById = (instanceKey: string, caseId: number) => {
  return errorSafe(async (dispatch: any, getState: any) => {
    dispatch(CaseViewTableRequestRemoveAction(instanceKey, caseId));

    try {
      const result = await caseListingApi.deleteCase(caseId) as any;
      const areaState = getState().favourite as UiState;
      const translationDescription = createDescriptionSelector(areaState.texts);
      if (result && result.status === 204) {
        dispatch(new SuccessNotification(
          translationDescription(caseListingTranslation.case_deleted_success)
        ));
        dispatch(CaseViewTableRemoveAction(instanceKey, caseId));
      }
    } catch (err) {
      dispatch({
        type: 'ERROR',
        error: err
      });
    }
  });
};

export const getSingleSupplierUrl = (instanceKey: string) => {
  return errorSafe(async (dispatch: any, getState: any) => {
    dispatch(CaseViewTableRequestSingleSupplierUrlAction(instanceKey));
    try {
      const singleSupplierUrl = await api.getCaseTableViewSingleSupplierUrl(1);
      dispatch(CaseViewTableReceiveSingleSupplierUrlAction(instanceKey, singleSupplierUrl));
    } catch (err) {
      dispatch({
        type: 'ERROR',
        error: err
      });
    }
  });
};

const toasts = toastFactory();
export const copyRiskEvaluation = (instanceKey: string, caseId: number, riskMatrixInstance: number,
  instanceCopyCmd: number, initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean) => {
  return errorSafe(async (dispatch: any, getState: any) => {
    try {
      const utcOffset = getState().app.settings.utcOffset;
      const selectedReport = getState().favourite?.instances[instanceKey]?.definition?.selectedReport;
      const navigateToRiskEvaluationSection = (draftId: number) => copyToResidual ?
        navigate.toCaseAddRiskEvaluations(draftId, sectionId[tableName.lossPotentialEvaluation].section) :
        navigate.toCaseAddLossPotentialEvaluation(draftId, sectionId[tableName.lossPotentialEvaluation].section);

      const navigateToRiskEvaluationSectionInModal = (draftId: number) => copyToResidual ?
            navigate.toCaseAddRiskEvaluationsInModal(draftId, sectionId[tableName.lossPotentialEvaluation].section) :
            navigate.toCaseAddLossPotentialEvaluationInModal(draftId, sectionId[tableName.lossPotentialEvaluation].section);          

      dispatch(new RequestCopyRiskEvaluation());
      const response = await lossPotentialEvaluationApi.addRiskEvaluationRead(caseId, riskMatrixInstance, instanceCopyCmd, initialValues,
        { utcOffset },
        copyToResidual);
      const pathname = new URL(response.headers.get('Location'))?.pathname?.split('/');
      const draftIdFromResponse = Number(pathname?.[pathname.length - 1]);
      dispatch(new ReceiveCopyRiskEvaluation());
      if (isCaseViewModalOpen()) {
        navigateToRiskEvaluationSectionInModal(draftIdFromResponse);
        return;
      }
      if (shouldOpenCaseInModal(!!selectedReport)) {
        navigateToRiskEvaluationSectionInModal(draftIdFromResponse);
        return;
      }
      navigateToRiskEvaluationSection(draftIdFromResponse);
      return;
    } catch (err) {
      toasts.dispatchError(getUserMessage(err.userMessages));
    }
  });
};

export const loadReportLayoutModel = (instanceKey: string, layoutTypeContextId?: number) => {
  return errorSafe(async (dispatch: any, getState: any) => {
    const selectedReport = selectSelectedReport(instanceKey)(getState());
    const layoutType = selectLayoutTypeForSelectedReport(instanceKey)(getState());
    const favouriteId = selectFavouriteId(instanceKey)(getState());

    if (favouriteId && selectedReport?.v && layoutType?.v) {
      dispatch(CaseViewTableRequestLayoutModelAction(instanceKey));
      const layoutModel = await caseListingApi.reportLayout(
        favouriteId,
        selectedReport.v,
        layoutType.v,
        layoutTypeContextId ?? reportLayoutTypeContext
      );
      dispatch(CaseViewTableReceiveLayoutModelAction(instanceKey, layoutModel));
    }
  });
};

export const ClearCaseViewTableAction = (instanceKey: string) => {
  return (dispatch: any, getState: any) => {
    dispatch(CaseViewTableClearAction(instanceKey));
  };
};

export const RequestCaseViewTableAction = (instanceKey: string): RequestCaseViewTableActionType => (
  {
    type: ACTION.REQUEST_CASE_VIEW_TABLE,
    payload: {
      instanceKey
    }
  }
);

export const CaseViewTableResultReplaceAction = ({ instanceKey, caseTableViewResults }:
  PayloadCaseTableViewResultType):
  CaseViewTableResultReplaceActionType => (
  {
    type: ACTION.REPLACE_CASE_VIEW_TABLE,
    payload: {
      instanceKey, caseTableViewResults
    }
  }
);

export const CaseViewTableResultAppendAction = ({ instanceKey, caseTableViewResults }:
  PayloadCaseTableViewResultType):
  CaseViewTableResultAppendActionType => (
  {
    type: ACTION.APPEND_CASE_VIEW_TABLE,
    payload: {
      instanceKey, caseTableViewResults
    }
  }
);

export const CaseViewTableFailExecuteFavouriteAction = (instanceKey: string, errorsExecuteFailed: string[], requestId?: number):
  FailExecuteFavouriteActionType => (
  {
    type: ACTION.FAIL_EXECUTE_FAVOURITE_CASE_VIEW_TABLE,
    payload: {
      instanceKey,
      errorsExecuteFailed,
      requestId
    }
  }
);

export const CaseViewTableClearAction = (instanceKey: string): CaseViewTableClearActionType => (
  {
    type: ACTION.CLEAR_CASE_VIEW_TABLE,
    payload: {
      instanceKey
    }
  }
);

export const CaseViewTableRequestRemoveAction = (instanceKey: string, caseId: number): CaseViewTableRequestRemoveItemActionType => (
  {
    type: ACTION.REQUEST_REMOVE_ITEM_CASE_VIEW_TABLE,
    payload: {
      instanceKey,
      caseId
    }
  }
);

export const CaseViewTableRemoveAction = (instanceKey: string, caseId: number): CaseViewTableRemoveItemActionType => (
  {
    type: ACTION.REMOVE_ITEM_CASE_VIEW_TABLE,
    payload: {
      instanceKey,
      caseId
    }
  }
);


export const CaseViewTableRequestSingleSupplierUrlAction = (instanceKey: string): CaseViewTableRequestSingleSupplierUrlActionType => (
  {
    type: ACTION.REQUEST_SINGLE_SUPPLIER_URL_CASE_VIEW_TABLE,
    payload: {
      instanceKey
    }
  }
);

export const CaseViewTableReceiveSingleSupplierUrlAction = (instanceKey: string, singleSupplierUrl: string): 
  CaseViewTableReceiveSingleSupplierUrlActionType => (
  {
    type: ACTION.RECEIVE_SINGLE_SUPPLIER_URL_CASE_VIEW_TABLE,
    payload: {
      instanceKey,
      singleSupplierUrl
    }
  }
);

export const CaseViewTableRequestLayoutModelAction = (instanceKey: string): CaseViewTableRequestLayoutModelActionType => (
  {
    type: ACTION.REQUEST_LAYOUT_MODEL,
    payload: {
      instanceKey
    }
  }
);

export const CaseViewTableReceiveLayoutModelAction = (instanceKey: string, layoutModel: any): CaseViewTableReceiveLayoutModelActionType => (
  {
    type: ACTION.RECEIVE_LAYOUT_MODEL,
    payload: {
      instanceKey,
      layoutModel
    }
  }
);
